import * as React from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CircleNotificationsIcon from '@mui/icons-material/CircleNotifications';
import MailIcon from '@mui/icons-material/Mail';
import TokenIcon from '@mui/icons-material/Token';
import { Button, CircularProgress, Menu, MenuItem } from '@mui/material';
import PAGE_ROUTES, {
  API_ROUTES,
  hasRole,
  ROLE_IDS,
} from '../../utils/constant';
import { useRouter } from '../../hooks/use-router';
import { getAuth, signOut } from 'firebase/auth'; // Import Firebase authentication
import useCommonDetails from '../../hooks/useCommonDetails';
import Iconify from '../iconify';
import ClockInOut from '../ClockInOut';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ContactsPopover from '../Notifications';
import PushNotificationManager from '../NotificationComponent';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import FullScreenDialog from '../FullScreenDialog';
import GlobalSearchContextProvider, {
  GlobalSearchContext,
} from '../../context/GlobalSearchContext';
import ViewBroadcastNotifications from '../ViewBroadcastNotifications';
import ReportIcon from '@mui/icons-material/Report';
import ReceiptIcon from '@mui/icons-material/Receipt';
const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}));

export default function MainContainer({
  children,
}: {
  children: React.ReactNode;
}) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const auth = getAuth(); // Get Firebase auth instance
  const currentUserInfo = useCommonDetails();
  const userRoles = [currentUserInfo.roleId];
  const userTeam: any = [currentUserInfo.teamId];
  const isGodMode = currentUserInfo.isGodMode || false;
  const { setIsCheckIn, isCheckIn, isLoading }: any =
    React.useContext(GlobalSearchContext);
  const router = useRouter();
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        localStorage.removeItem('auth');
        localStorage.removeItem('NotificationToken');
        console.log('User signed out');
        // Optionally redirect user after logout
        router.push(PAGE_ROUTES.LOGIN); // Redirect to login page after logout
      })
      .catch((error) => {
        console.error('Error signing out: ', error);
      });
  };

  const roleBasedListItems = [
    {
      text: 'Dashboard',
      route: PAGE_ROUTES.ADMINPRODUCTION,
      icon: <DashboardIcon />,
      roles: [ROLE_IDS.ADMIN, ROLE_IDS.SUPER_ADMIN, ROLE_IDS.MODERATOR], // admin and super_admin
    },
    {
      text: 'Games',
      route: PAGE_ROUTES.GAMES,
      icon: <SportsEsportsIcon />,
      roles: [
        ROLE_IDS.TAGGER,
        ROLE_IDS.ADMIN,
        ROLE_IDS.MODERATOR,
        ROLE_IDS.SUPER_ADMIN,
      ], // tagger, moderator, admin, super_admin
    },
    {
      text: 'Periods',
      route: PAGE_ROUTES.PERIODS,
      icon: <MailIcon />,
      roles: [
        ROLE_IDS.TAGGER,
        ROLE_IDS.ADMIN,
        ROLE_IDS.MODERATOR,
        ROLE_IDS.SUPER_ADMIN,
      ], // moderator, admin, tagger, super_admin
    },
    {
      text: 'All Users',
      route: PAGE_ROUTES.USERS,
      icon: <PeopleAltIcon />,
      roles: [ROLE_IDS.ADMIN, ROLE_IDS.SUPER_ADMIN], // admin and super_admin
    },
    {
      text: 'Shifts',
      route: PAGE_ROUTES.SHIFTS,
      icon: <Iconify icon="fluent:shifts-add-24-filled" width={25} />,
      roles: [ROLE_IDS.ADMIN, ROLE_IDS.SUPER_ADMIN], // admin and super_admin
    },
    {
      text: 'Teams',
      route: PAGE_ROUTES.TEAMS,
      icon: <Iconify icon="mdi:microsoft-teams" width={25} />,
      roles: [ROLE_IDS.SUPER_ADMIN], // super_admin
    },
    // {
    //   text: 'Notifications',
    //   route: PAGE_ROUTES.NOTIFICATION,
    //   icon: <CircleNotificationsIcon />,
    //   roles: [ROLE_IDS.ADMIN, ROLE_IDS.SUPER_ADMIN], // admin and super_admin
    // },
    {
      text: 'Broadcast Notification',
      route: PAGE_ROUTES.BROADCASTNOTIFICATION,
      icon: <AddAlertIcon />,
      roles: [ROLE_IDS.SUPER_ADMIN], //super_admin
    },
    {
      text: 'Invoice',
      route: PAGE_ROUTES.INVOICE,
      icon: <ReceiptIcon />,
      roles: [ROLE_IDS.SUPER_ADMIN],
      godModeOnly: true,
    },

    // {
    //   text: 'Report',
    //   route: PAGE_ROUTES.REPORT,
    //   icon: <AssessmentIcon />,
    //   roles: [ROLE_IDS.ADMIN, ROLE_IDS.SUPER_ADMIN], // admin and super_admin
    // },
    {
      text: 'Assign Request',
      route: PAGE_ROUTES.ASSIGNREQUEST,
      icon: <AssignmentTurnedInIcon />,
      roles: [ROLE_IDS.ADMIN, ROLE_IDS.SUPER_ADMIN], // admin and super_admin
      team: [3],
    },

    {
      text: 'Requested Token',
      route: PAGE_ROUTES.REQUESTEDTOKEN,
      icon: <TokenIcon />,
      roles: [ROLE_IDS.SUPER_ADMIN], // super_admin
      team: [3],
    },
    {
      text: 'Reports',
      route: PAGE_ROUTES.REPORTPAGE,
      icon: <ReportIcon />,
      roles: [ROLE_IDS.ADMIN, ROLE_IDS.SUPER_ADMIN], // admin and super_admin
      team: [3],
    },
  ];

  const roleBasedListSecoundItems = [
    {
      text: 'My Account',
      route: PAGE_ROUTES.MYACCOUNT,
      icon: <AccountCircleIcon />,
      roles: [
        ROLE_IDS.TAGGER,
        ROLE_IDS.ADMIN,
        ROLE_IDS.MODERATOR,
        ROLE_IDS.SUPER_ADMIN,
      ], // tagger, moderator, admin, super_admin
    },
  ];
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  if (isLoading) {
    return <CircularProgress />;
  }
  const ROLE_COLORS: Record<number, string> = {
    [ROLE_IDS.ADMIN]: 'red',
    [ROLE_IDS.SUPER_ADMIN]: 'red',
    [ROLE_IDS.MODERATOR]: 'green',
    [ROLE_IDS.TECHLEAD]: 'yellow',
    [ROLE_IDS.TAGGER]: 'default',
  };

  // Function to get role color
  const getRoleColor = (roleId: number) => ROLE_COLORS[roleId] || 'default';
  const getRoleName = (roleId: number) => {
    switch (roleId) {
      case ROLE_IDS.ADMIN:
        return 'Admin';
      case ROLE_IDS.MODERATOR:
        return 'Moderator';
      case ROLE_IDS.TAGGER:
        return 'Tagger';
      case ROLE_IDS.SUPER_ADMIN:
        return 'Super Admin';
      case ROLE_IDS.TECHLEAD:
        return 'Techlead';
      default:
        return 'Unknown'; // Fallback for unknown roles
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <FullScreenDialog
        openDiaLog={
          !isCheckIn &&
          !hasRole(currentUserInfo.roleId, [
            ROLE_IDS.ADMIN,
            ROLE_IDS.SUPER_ADMIN,
          ])
        }
      />

      <CssBaseline />
      <AppBar
        position="fixed"
        open={open}
        sx={{
          backgroundColor: () => {
            return getRoleColor(currentUserInfo.roleId);
          },
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={[
                {
                  marginRight: 5,
                },
                open && { display: 'none' },
              ]}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="h5"
                  component="div"
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {/* TMS Text */}
                  TMS
                  {/* Role in bottom-right as small text */}
                  <Typography
                    component="span"
                    variant="subtitle2"
                    sx={{
                      fontSize: '0.75rem', // Smaller font for the role
                      position: 'absolute',
                      bottom: 4, // Slightly lower to align it below TMS
                      right: 0, // Aligned to the right of TMS
                      transform: 'translateX(100%)', // Move the role text out of TMS box dynamically
                    }}
                  >
                    {`${getRoleName(currentUserInfo.roleId)}`}
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {' '}
            <ContactsPopover />
            {/* {ROLE_MAP[currentUserInfo.roleId] !== 'admin' &&  */}
            <ClockInOut />
            {/* } */}
            <Button
              sx={{ color: '#ffffff', whiteSpace: 'nowrap' }}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {roleBasedListItems
            .filter((item) => {
              const hasRole = isGodMode || item.roles.some((role) =>
                userRoles.includes(role)
              );
              const hasTeam = isGodMode ||
                !item.team || item.team.some((team) => userTeam.includes(team));
              const isVisible = !item.godModeOnly || isGodMode;
              return hasRole && hasTeam && isVisible;
            })
            .map((item) => (
              <ListItem
                key={item.text}
                disablePadding
                sx={{ display: 'block' }}
                onClick={() => router.push(item.route)}
              >
                <ListItemButton
                  selected={router.pathname === item.route}
                  sx={[
                    { minHeight: 48, px: 2.5 },
                    open
                      ? { justifyContent: 'initial' }
                      : { justifyContent: 'center' },
                    router.pathname === item.route && {
                      backgroundColor: '#C4CDD5 !important',
                    },
                  ]}
                >
                  <ListItemIcon
                    sx={[
                      { minWidth: 0, justifyContent: 'center' },
                      open ? { mr: 3 } : { mr: 'auto' },
                    ]}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={open ? { opacity: 1 } : { opacity: 0 }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
        <Divider />
        <List>
          {roleBasedListSecoundItems
            .filter((item) =>
              item.roles.some((role) => userRoles.includes(role))
            ) // Check user roles
            .map((item) => (
              <ListItem
                key={item.text}
                disablePadding
                sx={{ display: 'block' }}
                onClick={() => router.push(item.route)} // Navigate to the route
              >
                <ListItemButton
                  selected={router.pathname === item.route} // Highlight if current route matches
                  sx={[
                    { minHeight: 48, px: 2.5 },
                    open
                      ? { justifyContent: 'initial' }
                      : { justifyContent: 'center' },
                    router.pathname === item.route && {
                      backgroundColor: '#C4CDD5 !important',
                    }, // Light gray background for selected
                  ]}
                >
                  <ListItemIcon
                    sx={[
                      { minWidth: 0, justifyContent: 'center' },
                      open ? { mr: 3 } : { mr: 'auto' },
                    ]}
                  >
                    {item.icon} {/* Render the appropriate icon */}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text} // Display item text (Games, Periods, All Users)
                    sx={open ? { opacity: 1 } : { opacity: 0 }} // Control visibility based on the drawer's state
                  />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Drawer>
      <Box
        sx={{
          width: !open ? 'calc(100% - 70px)' : 'calc(100% - 240px)',
        }}
      >
        <Box component="main" sx={{ flexGrow: 1 }}>
          <DrawerHeader />
          <PushNotificationManager />
          <ViewBroadcastNotifications />
          <Box
            sx={{
              p: 3,
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
